import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";

const SubscribeSchema = z.object({
  email: z.string().trim().email("Invalid email").max(255),
  sourceUrl: z.string().max(2048).optional().default(""),
  website: z.string().max(0).optional().or(z.literal("")), // honeypot
});

export type SubscribeResult =
  | { ok: true; alreadySubscribed?: boolean }
  | { ok: false; error: "validation" | "server" }
  | { ok: false; error: "rate_limited"; retryAfterSeconds: number };

export const subscribeToAudience = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => {
    const parsed = SubscribeSchema.safeParse(input);
    if (!parsed.success) {
      throw new Error(JSON.stringify({ kind: "validation" }));
    }
    return parsed.data;
  })
  .handler(async ({ data }): Promise<SubscribeResult> => {
    // Rate-limit gate 1: per-IP
    const ip = getClientIp();
    const ipCheck = await checkRateLimit({
      name: "audience-ip",
      identifier: ip,
      windowSeconds: 60,
      maxRequests: 5,
    });
    if (!ipCheck.allowed) {
      return {
        ok: false,
        error: "rate_limited",
        retryAfterSeconds: ipCheck.retryAfterSeconds,
      };
    }

    // Honeypot: silently succeed without writing anything
    if (data.website && data.website.length > 0) {
      return { ok: true };
    }

    // Rate-limit gate 2: per-email
    const emailCheck = await checkRateLimit({
      name: "audience-email",
      identifier: data.email.toLowerCase().trim(),
      windowSeconds: 60,
      maxRequests: 3,
    });
    if (!emailCheck.allowed) {
      return {
        ok: false,
        error: "rate_limited",
        retryAfterSeconds: emailCheck.retryAfterSeconds,
      };
    }

    console.log(
      `[audience] subscribe email=${data.email} sourceUrl=${data.sourceUrl} at=${new Date().toISOString()}`,
    );

    try {
      const { error } = await supabaseAdmin
        .from("audience_subscribers")
        .insert({
          email: data.email,
          source_url: data.sourceUrl,
        });

      if (error) {
        // Unique violation = already subscribed. Treat as success.
        if (
          error.code === "23505" ||
          /duplicate key|unique constraint/i.test(error.message)
        ) {
          console.log(`[audience] already subscribed email=${data.email}`);
          return { ok: true, alreadySubscribed: true };
        }
        console.error("[audience] insert failed", error);
        return { ok: false, error: "server" };
      }
    } catch (err) {
      console.error("[audience] insert threw", err);
      return { ok: false, error: "server" };
    }

    return { ok: true };
  });
